import React, { useState, useEffect, cloneElement, useCallback, useMemo, useRef } from 'react';
import { debounce, isNil } from 'lodash';
import Select from './BaseSelect';
import Icon from '../icons/BaseIcon';
import { requestStatuses } from 'shared/../constants/store';
import MultiSelect from 'shared/inputs/BaseMultiSelect';
import { ARROWDOWN_KEY, ARROWUP_KEY } from 'src/shared/constants/keyCodes';

/**
 * An async select component. Loads and displays data when User enters a search string.
 *
 * @typedef {React.ComponentProps<Select>} BaseSelectProps
 *
 * @typedef {Object} AdditionalAsyncSelectProps
 * @property {Function} getData A callback to load data by a search string.
 * @property {boolean} [loadInitialData={false}] Indicates if data need to be loaded the data for the initial values from the api. False by default.
 * @property {boolean} [reloadOnOpen={false}] Indicates if data need to be loaded when the dropdown is open. False by default.
 * @property {boolean} [fetchOnMount={false}] Indicates if data need to be loaded when the dropdown mounted. False by default.
 * @property {boolean} [forceReload] Indicates if data need to be reloaded.
 * @property {Function} [didLoad] A callback when data is loaded.
 * @property {boolean} [shouldFetchOnChange={true}] Indicates if data needs to be reloaded when User changes a search string. True by default.
 *
 * @param {BaseSelectProps & AdditionalAsyncSelectProps} props Component props.
 * @returns {React.ReactElement} Async select component.
 */
const AsyncSelect = ({
  getData,
  loadInitialData,
  onChange,
  value,
  dataItemKey = 'value',
  textField = 'description',
  reloadOnOpen = false,
  fetchOnMount = false,
  forceReload,
  didLoad,
  onBlur,
  onFocus,
  shouldFetchOnChange = true,
  ...props
}) => {
  const [search, setSearch] = useState(null);

  const [forceShowOptions, setForceShowOptions] = useState(false); // Forces dropdown to show if arrow keys are used

  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(requestStatuses.INIT);
  const initialDataLoaded = useRef(false);

  const reset = () => {
    setError(null);
    setStatus(requestStatuses.INIT);
  };

  const listNoDataRender = (element) => {
    const getIcon = () => {
      if (status === requestStatuses.FAILED) {
        return 'error';
      } if (status === requestStatuses.PENDING) {
        return 'loading';
      }
      return 'info';
    };

    const getMessage = () => {
      if (status === requestStatuses.FAILED) {
        return error.message;
      }
      if (status === requestStatuses.PENDING) {
        return 'Searching';
      }

      if (reloadOnOpen) {
        return 'No Items';
      }

      if (isNil(search) || search.trim().length === 0) {
        return 'Type to Search';
      }

      return 'No Items';
    };

    const noData = (
      <div>
        <div>
          <Icon name={getIcon()} />
        </div>
        <div>{getMessage()}</div>
      </div>
    );
    return cloneElement(element, { ...element.props }, noData);
  };

  const handleChange = (e) => {
    const { nativeEvent } = e;

    const isSetByArrowKey = nativeEvent?.key === ARROWDOWN_KEY || nativeEvent?.key === ARROWUP_KEY;
    setForceShowOptions(isSetByArrowKey); // Force show options if value is set by arrow keys
    setSearch(null);
    if (onChange) {
      onChange(e);
    }
  };

  const debounced = useCallback(
    debounce((search, allowEmpty = false) => {
      if (!allowEmpty && (isNil(search) || search.trim().length === 0)) {
        return null;
      }

      setStatus(requestStatuses.PENDING);
      return getData(search ? search.trim() : undefined)
        .then((data) => {
          setData(data ?? []);
          setStatus(requestStatuses.SUCCESS);
        })
        .catch((error) => {
          setError(error);
          setStatus(requestStatuses.FAILED);
        });
    }, 300),
    [],
  );

  useEffect(() => {
    if (loadInitialData && !initialDataLoaded.current && !isNil(value)) {
      const ids = props?.multiSelect
        ? value?.map((v) => (typeof v === 'object' ? v[dataItemKey] : v))
        : typeof v === 'object' ? [value[dataItemKey]] : [value];

      getData(null, { ids })
        .then((data) => {
          setData(data ?? []);
          setStatus(requestStatuses.SUCCESS);
        })
        .catch((error) => {
          setError(error);
          setStatus(requestStatuses.FAILED);
        });
    }
    initialDataLoaded.current = true;
  }, [value]);

  // Initial data
  useEffect(() => {
    if (fetchOnMount) {
      debounced(null, true);
    }
  }, []);

  useEffect(() => {
    if (forceReload) {
      debounced(null, true);

      if (didLoad) {
        didLoad();
      }
    }
  }, [forceReload]);

  const onFilterChange = (e) => {
    setSearch(e?.filter?.value ?? null);
  };

  useEffect(() => {
    if ((isNil(search) || search.trim() === '') && data.length > 0) {
      if (reloadOnOpen) {
        debounced(null, true);
        return;
      }
      reset();
      return;
    }
    if (shouldFetchOnChange) { debounced(search, reloadOnOpen); }
  }, [search]);

  const onOpen = () => {
    if (reloadOnOpen) {
      debounced('', true);
    }
  };

  const oldMultiSelectValue = useRef(null);
  const selected = useMemo(() => {
    if (props.multiSelect) {
      if ((Array.isArray(value) && value.every((x) => typeof x === 'object')) || isNil(value)) {
        oldMultiSelectValue.current = value;
        return value;
      }
      if (Array.isArray(value) && value.length > 0 && data?.length > 0) {
        const values = [];
        value.forEach((v) => {
          if (oldMultiSelectValue.current) {
            const s = oldMultiSelectValue.current.find((s) => s[dataItemKey] === v);
            if (s) {
              values.push(s);
              return;
            }
          }
          const newVal = data?.find((item) => item[dataItemKey] === v);
          if (newVal) {
            values.push(newVal);
          }
        });

        oldMultiSelectValue.current = values;
        return values;
      }
      oldMultiSelectValue.current = null;
      return null;
    }
    return typeof value === 'object' || isNil(value)
      ? value
      : data.find((item) => item[dataItemKey] === value);
  }, [value, data, props.multiSelect]);

  const options = useMemo(
    () => (!forceShowOptions && ((!search && !(reloadOnOpen || fetchOnMount)) || status !== requestStatuses.SUCCESS)
      ? []
      : data.filter((item) => (!isNil(item[textField])))),
    [data, search, forceShowOptions, status, reloadOnOpen, fetchOnMount],
  );

  const onWrapperClick = (e) => {
    e.nativeEvent.target.select(); // Highlight text in input
    setForceShowOptions(false);
  };

  const handleBlur = (e) => {
    setForceShowOptions(false);
    if (onBlur) {
      onBlur(e);
    }
    setSearch(null);
  };

  const handleFocus = useCallback((e) => {
    setForceShowOptions(false);
    if (onFocus) {
      onFocus(e);
    }
  }, []);

  if (props.multiSelect) {
    return (
      <MultiSelect
        {...props}
        data={options}
        loading={status === requestStatuses.PENDING}
        listNoDataRender={listNoDataRender}
        filterable
        filter={search}
        onFilterChange={onFilterChange}
        onChange={handleChange}
        value={selected}
        onOpen={onOpen}
        dataItemKey={dataItemKey}
        textField={textField}
        onBlur={handleBlur}
        onFocus={handleFocus}
      />
    );
  }

  return (
    <Select
      {...props}
      data={options}
      loading={status === requestStatuses.PENDING}
      listNoDataRender={listNoDataRender}
      filterable
      filter={search}
      onFilterChange={onFilterChange}
      onChange={handleChange}
      value={selected}
      onOpen={onOpen}
      dataItemKey={dataItemKey}
      textField={textField}
      id={props.id}
      onBlur={handleBlur}
      onFocus={handleFocus}
      closeOnClear
      onWrapperClick={onWrapperClick}
      suggest={false}
      focusedItemIndex={() => 0}
      selectFirstItemOnFocus
    />
  );
};

export default AsyncSelect;
